import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext, Controller } from 'react-hook-form';
import { Checkbox, InputLabel, makeStyles } from '@material-ui/core';
import { DateRangeOutlined } from '@material-ui/icons';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import ko from 'date-fns/locale/ko';
import 'date-fns';
import { RegisterRfqInput } from '@generated/graphql';
import { getMinDateFromToday } from '@utils/date';
import { RootState } from '@modules/index';
import { colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import FormInputLayout from '../Layout/FormInputLayout';
import { RequestInputCommonProps } from './types';

const DueDateInput: React.FC<RequestInputCommonProps> = ({ error }) => {
  const dueDateDefaultValue = useSelector<RootState, string>(
    (state) => state.request.dueDate
  );
  const dueNegotiableDefaultValue = useSelector<RootState, boolean>(
    (state) => state.request.dueNegotiable ?? false
  );
  const [selectedDate, setSelectedDate] = useState<string>(
    dueDateDefaultValue.length === 0
      ? getMinDateFromToday(7).toISOString()
      : dueDateDefaultValue
  );
  const [dueNegotiable, setDueNegotiable] = useState<boolean>(
    dueNegotiableDefaultValue ?? false
  );
  const { control } = useFormContext<RegisterRfqInput>();
  const { dueDateInputStyle, dueNegotiableBox } = useStyles();

  return (
    <>
      <FormInputLayout label="납기일" required={true}>
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ko}>
          <Controller
            name="dueDate"
            control={control}
            rules={{ required: '납기일을 선택해주세요' }}
            render={(renderProps) => (
              <DatePicker
                className={dueDateInputStyle}
                error={!!error}
                helperText={error}
                inputVariant="outlined"
                fullWidth
                data-cy="rfq-dueDate"
                InputProps={{
                  endAdornment: <DateRangeOutlined />
                }}
                disableToolbar
                variant="inline"
                format="~ yyyy. MM. dd."
                margin="none"
                minDate={getMinDateFromToday(7)}
                autoOk
                value={selectedDate}
                onChange={(date) => {
                  if (date) {
                    const dateValue = date?.toISOString();
                    setSelectedDate(dateValue);
                    renderProps.onChange(dateValue);
                  }
                }}
              />
            )}
          />
        </MuiPickersUtilsProvider>
        <Controller
          control={control}
          defaultValue={dueNegotiableDefaultValue ?? false}
          name="dueNegotiable"
          render={({ onChange, value }) => (
            <InputLabel className={dueNegotiableBox}>
              <Checkbox
                data-cy="rfq-dueNegotiable"
                size="small"
                color="primary"
                checked={dueNegotiable}
                onChange={(e) => {
                  setDueNegotiable((prev) => !prev);
                  onChange(!value);
                }}
              />
              <CustomizedTypography variant="body2" color="textPrimary">
                납기일 협의 가능
              </CustomizedTypography>
            </InputLabel>
          )}
        />
      </FormInputLayout>
    </>
  );
};

export default DueDateInput;

const useStyles = makeStyles((theme) => ({
  dueDateInputStyle: {
    '& input': {
      padding: 12,
      textAlign: 'left',
      '&:hover': {
        cursor: 'pointer'
      }
    },
    '& .MuiOutlinedInput-adornedEnd': {
      '&:hover': {
        cursor: 'pointer'
      }
    },
    '& .MuiInputBase-root': {
      alignItems: 'center',
      borderRadius: 8
    },
    '& ::placeholder': {
      color: colorSet.gray500
    }
  },
  dueNegotiableBox: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 8,
    transform: 'translateX(-9px)',
    '&:hover': {
      cursor: 'pointer'
    }
  }
}));
